import { FirebaseError } from 'firebase/app';
import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  type DocumentData,
  type Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { ALL_PERMISSIONS, type Permission } from './appUser.service';

const ORGS_COLLECTION = 'orgs';
const ROLES_COLLECTION = 'roles';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Role {
  id: string;
  name: string;
  permissions: Permission[];
  isSystem: boolean;
  createdAt: Timestamp;
}

export interface RoleServiceError {
  code: string;
  message: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function docToRole(id: string, data: DocumentData): Role {
  return {
    id,
    name: data.name as string,
    permissions: (data.permissions as Permission[]) ?? [],
    isSystem: (data.isSystem as boolean) ?? false,
    createdAt: data.createdAt as Timestamp,
  };
}

function toServiceError(error: unknown): RoleServiceError {
  if (error instanceof FirebaseError) {
    return { code: error.code, message: error.message };
  }
  return { code: 'unknown', message: 'An unexpected error occurred' };
}

// ─── Role functions ───────────────────────────────────────────────────────────

/**
 * Get all roles for an org (system + custom)
 */
export async function getRoles(orgId: string): Promise<Role[]> {
  try {
    const rolesRef = collection(db, ORGS_COLLECTION, orgId, ROLES_COLLECTION);
    const snapshot = await getDocs(rolesRef);
    return snapshot.docs.map(doc => docToRole(doc.id, doc.data()));
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * Create a custom role in an org
 */
export async function createRole(
  orgId: string,
  name: string,
  permissions: Permission[]
): Promise<string> {
  try {
    // Validate permissions are all valid
    const invalidPerms = permissions.filter(p => !ALL_PERMISSIONS.includes(p));
    if (invalidPerms.length > 0) {
      throw new Error(`Invalid permissions: ${invalidPerms.join(', ')}`);
    }

    const roleRef = doc(collection(db, ORGS_COLLECTION, orgId, ROLES_COLLECTION));
    await setDoc(roleRef, {
      name,
      permissions,
      isSystem: false,
      createdAt: serverTimestamp(),
    });

    return roleRef.id;
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * Update a custom role (name + permissions)
 * System roles cannot be updated
 */
export async function updateRole(
  orgId: string,
  roleId: string,
  name: string,
  permissions: Permission[]
): Promise<void> {
  try {
    // Validate permissions
    const invalidPerms = permissions.filter(p => !ALL_PERMISSIONS.includes(p));
    if (invalidPerms.length > 0) {
      throw new Error(`Invalid permissions: ${invalidPerms.join(', ')}`);
    }

    const roleRef = doc(db, ORGS_COLLECTION, orgId, ROLES_COLLECTION, roleId);
    await updateDoc(roleRef, {
      name,
      permissions,
    });
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * Delete a custom role
 * System roles cannot be deleted
 * Check with service caller that no members have this role before deleting
 */
export async function deleteRole(orgId: string, roleId: string): Promise<void> {
  try {
    const roleRef = doc(db, ORGS_COLLECTION, orgId, ROLES_COLLECTION, roleId);
    await deleteDoc(roleRef);
  } catch (error) {
    throw toServiceError(error);
  }
}
